import { createApi } from '@reduxjs/toolkit/query/react'
import { ASSESSMENT_TYPES, CLIENT_LOOKUP, DISCIPLINE_AREA_GOALS, DISCIPLINE_AREAS, FORMAL_ASSESSMENTS, FUNDING_SOURCE_UTILITY_LIST, GOAL_CONTEXTS, PARENT_LOOKUP, PROGRAM_LOOKUP, AUTOCOMPLETE, TEACHER_LOOKUP, UPLOAD_FILE, LESSONS_LIST, SCHOOL_LOOKUP, THERAPIST_LOOKUP, STAFF_LOOKUP, APPRENTICE_LOOKUP, DISCIPLINES_LOOKUP, SCHOOL_TEACHER_LOOKUP, CLIENT_GUARDIANS_LOOKUP, CHATTER_PROGRAM_CLIENT_LOOKUP, CLIENT_SERVICE_STATUS_LOOKUP, ASSIGNED_SCHOOLS_LOOKUP, REPORT_OPTIONS, DISCIPLINE_TAGS, SUBADMIN_LOOKUP, ASSIGNED_SCHOOL_DIVISIONS_LOOKUP, SUPERADMIN_LOOKUP } from "config/apiPaths"
import { pickBy } from "lodash";
import axiosBaseQuery from "utils/axios/axiosBaseQuery"

export const utilityApi = createApi({
  reducerPath: 'utilityApi',
  baseQuery: axiosBaseQuery({
    baseUrl: "/utility",
  }),
  tagTypes: ["LessonsLookup", "SchoolLookup", "TherapistLookup", "ProgramClientLookup", "ClientServiceStatusLookup", "MdtStatusList"],
  endpoints: (builder) => ({

    // upload file
    uploadFile: builder.mutation({
      query: ({ file, acl, fileOriginalFormat = false }) => {
        // Ensure acl and file are passed in
        if (!acl) {
          throw new Error("acl is required");
        }
        if (!file) {
          throw new Error("file not provided to upload");
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("acl", acl);

        if (fileOriginalFormat) {
          formData.append("fileOriginalFormat", fileOriginalFormat);
        }

        return {
          url: UPLOAD_FILE,
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        };
      },
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    // teachers lookup
    teacherLookup: builder.query({
      query: () => ({
        method: "GET",
        url: `${TEACHER_LOOKUP}`
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    // parents lookup
    parentLookup: builder.query({
      query: () => ({
        method: "GET",
        url: `${PARENT_LOOKUP}`
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    getAssessmentTypes: builder.query({
      query: () => ({
        method: "GET",
        url: ASSESSMENT_TYPES,
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    getDisciplineAreas: builder.query({
      query: (disciplineId) => ({
        method: "GET",
        url: `${DISCIPLINE_AREAS}/${disciplineId}`,
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    getDisciplineTags: builder.query({
      query: (disciplineId) => ({
        method: "GET",
        url: `${DISCIPLINE_TAGS}/${disciplineId}`,
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    getGoals: builder.query({
      query: ({ disciplineId, areaId }) => ({
        method: "GET",
        url: `${DISCIPLINE_AREA_GOALS}/${disciplineId}/${areaId}`,
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    getGoalContexts: builder.query({
      query: ({ goalId }) => ({
        method: "GET",
        url: `${GOAL_CONTEXTS}/${goalId}`,
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    getFormalAssessments: builder.query({
      query: () => FORMAL_ASSESSMENTS,
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    autocomplete: builder.mutation({
      query: ({ search, type, assessment_type, question_id }) => {
        let payload = pickBy({ search, assessment_type, question_id });
        return ({
          method: "POST",
          url: `/${type}${AUTOCOMPLETE}`,
          data: payload
        })
      }
    }),

    divisionLookup: builder.query({
      query: (data) => "/divisions",
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    // Get file url
    getFileUrl: builder.query({
      query: (key) => ({
        method: "GET",
        url: `${UPLOAD_FILE}/${key}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),

    // funding source list
    getFundingSourceList: builder.query({
      query: () => ({
        method: "GET",
        url: `${FUNDING_SOURCE_UTILITY_LIST}`,
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["FundingSource"]
    }),

    // MDT therapist status
    getMdtTherapistStatusList: builder.query({
      query: () => ({
        method: "GET",
        url: "/status/mdt_checklist/chatter"
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["MdtTherapistStatus"]
    }),

    // program lookup
    programLookup: builder.query({
      query: (search) => ({
        method: "GET",
        url: `${PROGRAM_LOOKUP}${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["ProgramLookup"]
    }),

    // client lookup
    clientLookupSearch: builder.query({
      query: ({ search, client }) => ({
        method: "GET",
        url: `${CLIENT_LOOKUP}/${client}/lookup${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),

    // lessons list
    getLessonsList: builder.query({
      query: ({ search = "", tag_id = [], page, limit, sort = { column: "id", direction: "DESC" } } = {}) => ({
        method: "POST",
        url: `${LESSONS_LIST}`,
        data: { search, tag_id, page, limit, sort }
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["LessonsLookup"]
    }),

    // school lookup
    schoolLookup: builder.query({
      query: (search) => ({
        method: "GET",
        url: `${SCHOOL_LOOKUP}${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["SchoolLookup"]
    }),

    // therapist lookup
    therapistLookup: builder.query({
      query: ({ skip_test = false, notes_collab = false } = {}) => ({
        method: "GET",
        url: `${THERAPIST_LOOKUP}${skip_test ? "?skip_test=true" : ""}${notes_collab ? "&notes_collab=true" : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["TherapistLookup"]
    }),

    // staff lookup
    staffLookup: builder.query({
      query: ({ clientType, search }) => ({
        method: "GET",
        url: `${STAFF_LOOKUP}/${clientType}/${search || ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),
    // staff list as per school and div
    getStaffListSchool: builder.mutation({
      query: ({ payload }) => ({
        method: "PATCH",
        // ContentType:"application/json",
        url: `/support_staffTheraphist`,
        data: payload
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    // apprentice lookup
    apprenticeLookup: builder.query({
      query: (search) => ({
        method: "GET",
        url: `${APPRENTICE_LOOKUP}${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),
    // disciplines lookup
    disciplinesLookup: builder.query({
      query: () => ({
        method: "GET",
        url: `${DISCIPLINES_LOOKUP}`
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    // teachers lookup
    teacherLookupWithSchool: builder.query({
      query: (schoolId) => ({
        method: "GET",
        url: `${SCHOOL_TEACHER_LOOKUP}/${schoolId}`
      }),
      transformResponse: (response) => {
        return response?.data;
      }
    }),

    // client guardians lookup
    clientGuardiansLookup: builder.query({
      query: (client) => ({
        method: "GET",
        url: `${CLIENT_GUARDIANS_LOOKUP}/${client}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),

    // chatter program client lookup
    chatterProgramClientLookup: builder.query({
      query: ({ clientId, params }) => ({
        method: "GET",
        url: `${CHATTER_PROGRAM_CLIENT_LOOKUP}/${clientId}`,
        params
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["ProgramClientLookup"]
    }),

    // Clients service status list 
    clientsServiceStatusList: builder.query({
      query: ({ module, client_type }) => ({
        method: "GET",
        url: `${CLIENT_SERVICE_STATUS_LOOKUP}/${module}/${client_type}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["ClientServiceStatusLookup"]
    }),

    // MDT status list
    getMdtStatusList: builder.query({
      query: () => ({
        method: "GET",
        url: "/status/mdt/chatter"
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["MdtStatusList"]
    }),

    // Get assignment list
    getConsentformList: builder.query({
      query: ({ client_id }) => ({
        method: "GET",
        url: `/consent_forms_list/${client_id}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),
    getConsentformData: builder.query({
      query: ({ parent_id, consent_id }) => ({
        method: "GET",
        url: `/consent_forms_data/${parent_id}/${consent_id}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),
    //assigned school lookup
    assignedSchoolsLookup: builder.query({
      query: (search) => ({
        method: "GET",
        url: `${ASSIGNED_SCHOOLS_LOOKUP}${search ? `/${search}` : ""}?assigned_schools=true`
      }),
      transformResponse: (response) => {
        return response?.data?.records;
      },
    }),
    //assigned division lookup
    assignedSchoolsDivisionLookup: builder.query({
      query: (search) => ({
        method: "GET",
        url: `${ASSIGNED_SCHOOL_DIVISIONS_LOOKUP}${search ? `/${search}` : ""}`
      }),
      transformResponse: (response) => {
        return response?.data?.records;
      },
    }),
    getReportOptions: builder.query({
      query: () => ({
        method: "GET",
        url: REPORT_OPTIONS
      }),
      transformResponse: (response) => {
        return response?.data;
      },
    }),
    // sub-admin lookup
    subAdminLookup: builder.query({
      query: ({ search, skip_test = false, notes_collab = false }) => ({
        method: "GET",
        url: `${SUBADMIN_LOOKUP}${search ? `/${search}` : ""}${skip_test ? "?skip_test=true" : ""}${notes_collab ? "&notes_collab=true" : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["SubAdminLookup"]
    }),

    // super-admin lookup
    superAdminLookup: builder.query({
      query: ({ search, skip_test }) => ({
        method: "GET",
        url: `${SUPERADMIN_LOOKUP}${search ? `/${search}` : ""}${skip_test ? "?skip_test=true" : ""}`
      }),
      transformResponse: (response) => {
        return response?.data;
      },
      providesTags: ["SuperAdminLookup"]
    }),
    getSchoolDivisonList: builder.query({
      query: (id) => ({
        method: "GET",
        url: `/assigneddivisions`
      }),
      transformResponse: (response) => {
        return response.data.records;
      },
      providesTags: ["SchoolDivisons"]
    }),
  }),
})

export const {
  useUploadFileMutation,
  useTeacherLookupQuery,
  useParentLookupQuery,
  useGetAssessmentTypesQuery,
  useGetDisciplineAreasQuery,
  useGetGoalsQuery,
  useGetGoalContextsQuery,
  useGetFormalAssessmentsQuery,
  useLazyGetFormalAssessmentsQuery,
  useAutocompleteMutation,
  useDivisionLookupQuery,
  useLazyGetFileUrlQuery,
  useGetFundingSourceListQuery,
  useLazyProgramLookupQuery,
  useGetMdtTherapistStatusListQuery,
  useLazyClientLookupSearchQuery,
  useGetLessonsListQuery,
  useLazySchoolLookupQuery,
  useLazyTherapistLookupQuery,
  useTherapistLookupQuery,
  useLazyStaffLookupQuery,
  useLazyApprenticeLookupQuery,
  useDisciplinesLookupQuery,
  useLazyTeacherLookupWithSchoolQuery,
  useLazyClientGuardiansLookupQuery,
  useSchoolLookupQuery,
  useChatterProgramClientLookupQuery,
  useClientsServiceStatusListQuery,
  useGetMdtStatusListQuery,
  useGetStaffListSchoolMutation,
  useGetConsentformListQuery,
  useGetConsentformDataQuery,
  useAssignedSchoolsLookupQuery,
  useLazyAssignedSchoolsLookupQuery,
  useAssignedSchoolsDivisionLookupQuery,
  useGetReportOptionsQuery,
  useGetDisciplineTagsQuery,
  useSubAdminLookupQuery,
  useSuperAdminLookupQuery,
  useGetSchoolDivisonListQuery
} = utilityApi;